// v2 paving — a flagstone court: large irregular cool blue-grey flagstone slabs
// laid in three offset courses over a mossy joint bed, edged on open sides by a
// row of raised slate setts. Stone colour and stand-height vary by a
// deterministic hash. Ported 1:1 from age-of-mcp's v2/paving.ts. The model
// library has no terrain and the per-tile network params (connections / heights)
// are not modelled here, so this renders the default lone-tile look the flattened
// file bakes: flat ground (the source's `terrainSurfaceY` reduces to 0, and
// `warpedSlab` / `warpedPolySlab` reduce to the flat forms inlined here) with no
// connections (all four sides open -> the flagstones pull back from the band and
// all four sett edges show). Build stages: screed, firmer screed, moss bed with
// the first course, then the full court.
import * as THREE from "three";
import { defineModel, roadwork, type Part } from "../../lib";

// Cool blue-grey flagstone palette over a mossy bed.
const MOSS  = "#5a6549";   // mossy joint bed showing between the slabs
const FLAG  = ["#8d99a4", "#7b8893", "#9aa5ae", "#75818c", "#a3adb5"];
const SLATE = ["#4a525b", "#555e67"];   // alternating edging setts

// Geometry constants — local model units (1 unit = 1 tile, scale 1).
const HALF  = 0.5;     // the bed reaches the tile edge so neighbours touch
const LIFT  = 0.05;    // proud of the terrain — keep = renderer PAVING_LIFT
const BED_H = 0.15;    // moss-bed thickness, base buried in the ground
const JOINT = 0.016;   // moss reveal around every flagstone

// Edging band on open sides: slate setts standing a little proud of the
// stones, and the stone field pulled back clear of them.
const FIELD_IN  = 0.376;
const SETT_IN   = 0.394;
const SETT_OUT  = 0.496;
const SETT_POS  = [-0.33, -0.11, 0.11, 0.33];
const SETT_LEN  = 0.115;

type V = readonly [number, number, number];
type V2 = readonly [number, number];

const SQUARE: readonly V2[] = [
  [-HALF, -HALF], [HALF, -HALF], [HALF, HALF], [-HALF, HALF],
];

// Course grid: three rows of flagstones with offset joints.
const ROWS = [-HALF, -0.165, 0.158, HALF] as const;
const COLS: ReadonlyArray<readonly number[]> = [
  [-HALF, -0.118, 0.196, HALF],
  [-HALF, 0.042, HALF],
  [-HALF, -0.205, 0.131, HALF],
];

function quad(out: number[], p0: V, p1: V, p2: V, p3: V): void {
  out.push(...p0, ...p1, ...p2, ...p0, ...p2, ...p3);
}
function tri(out: number[], a: V, b: V, c: V): void {
  out.push(...a, ...b, ...c);
}
function signedArea(pts: readonly V2[]): number {
  let a = 0;
  for (let i = 0; i < pts.length; i++) {
    const [x0, z0] = pts[i]!;
    const [x1, z1] = pts[(i + 1) % pts.length]!;
    a += x0 * z1 - x1 * z0;
  }
  return a / 2;
}

// The flat reduction of the source's `warpedSlab` (flat corners): top at y =
// `lift`, bottom at -thickness.
function slab(thickness: number, color: string, lift: number): Part {
  const top = lift, yb = -thickness, h = HALF;
  const tSW: V = [-h, top, -h], tSE: V = [h, top, -h];
  const tNW: V = [-h, top,  h], tNE: V = [h, top,  h];
  const bSW: V = [-h, yb, -h], bSE: V = [h, yb, -h];
  const bNW: V = [-h, yb,  h], bNE: V = [h, yb,  h];
  const pos: number[] = [];
  quad(pos, tSW, tNW, tNE, tSE);
  quad(pos, bSW, bSE, bNE, bNW);
  quad(pos, tNW, bNW, bNE, tNE);
  quad(pos, tSE, bSE, bSW, tSW);
  quad(pos, tSE, tNE, bNE, bSE);
  quad(pos, tSW, bSW, bNW, tNW);
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute("position", new THREE.Float32BufferAttribute(pos, 3));
  return { geometry, color, position: [0, 0, 0] };
}

// The flat reduction of `warpedPolySlab`: a convex polygon top fan at y = `lift`
// (flat ground) over a vertical wall skirt dropping `thickness` below 0.
function polySlab(points: readonly V2[], thickness: number, color: string, lift: number): Part {
  const ring = signedArea(points) > 0 ? [...points].reverse() : [...points];
  const yb = -thickness;
  const topY = lift;
  let cx = 0, cz = 0;
  for (const p of ring) { cx += p[0]; cz += p[1]; }
  const pivot: V2 = [cx / ring.length, cz / ring.length];
  const pv: V = [pivot[0], topY, pivot[1]];
  const pos: number[] = [];
  for (let i = 0; i < ring.length; i++) {
    const a = ring[i]!, b = ring[(i + 1) % ring.length]!;
    const ta: V = [a[0], topY, a[1]];
    const tb: V = [b[0], topY, b[1]];
    const ba: V = [a[0], yb, a[1]];
    const bb: V = [b[0], yb, b[1]];
    tri(pos, pv, ta, tb);              // top fan (+Y)
    quad(pos, tb, ta, ba, bb);         // outward wall skirt
  }
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute("position", new THREE.Float32BufferAttribute(pos, 3));
  return { geometry, color, position: [0, 0, 0] };
}

// The flagstone slabs of the first `rowCount` courses, inset by the moss joint
// and pulled clear of the sett band on the open sides (all four, no connections).
function flagstones(rowCount: number): Part[] {
  const out: Part[] = [];
  for (let r = 0; r < rowCount; r++) {
    const xs = COLS[r]!;
    for (let i = 0; i + 1 < xs.length; i++) {
      let x0 = xs[i]! + JOINT, x1 = xs[i + 1]! - JOINT;
      let z0 = ROWS[r]! + JOINT, z1 = ROWS[r + 1]! - JOINT;
      // Pull back from the edging band on every (open) side.
      x1 = Math.min(x1, FIELD_IN);
      x0 = Math.max(x0, -FIELD_IN);
      z1 = Math.min(z1, FIELD_IN);
      z0 = Math.max(z0, -FIELD_IN);
      const hS = 0.024 + roadwork.h01(i, r, 17) * 0.012;
      const col = FLAG[Math.floor(roadwork.h01(i, r, 53) * FLAG.length) % FLAG.length]!;
      const pts: readonly V2[] = [[x0, z0], [x1, z0], [x1, z1], [x0, z1]];
      out.push(polySlab(pts, 0.1, col, LIFT + hS));
    }
  }
  return out;
}

// Rows of raised slate setts along every open side (all four, no connections).
function edgingSetts(): Part[] {
  const out: Part[] = [];
  const addSide = (positive: boolean, alongZ: boolean): void => {
    const a0 = positive ? SETT_IN : -SETT_OUT;
    const a1 = positive ? SETT_OUT : -SETT_IN;
    for (let k = 0; k < SETT_POS.length; k++) {
      const p = SETT_POS[k]!;
      const lo = p - SETT_LEN / 2, hi = p + SETT_LEN / 2;
      const pts: readonly V2[] = alongZ
        ? [[a0, lo], [a1, lo], [a1, hi], [a0, hi]]   // E/W band: long axis = z
        : [[lo, a0], [hi, a0], [hi, a1], [lo, a1]];  // N/S band: long axis = x
      const hK = 0.042 + (k % 2) * 0.007;
      out.push(polySlab(pts, 0.1, SLATE[k % 2]!, LIFT + hK));
    }
  };
  addSide(true, true);    // E
  addSide(false, true);   // W
  addSide(true, false);   // N
  addSide(false, false);  // S
  return out;
}

function stage(s: number): Part[] {
  if (s <= 0) return [slab(0.08, "#6a5f50", LIFT * 0.4)];
  if (s === 1) return [slab(0.10, "#7a7163", LIFT * 0.7)];
  const bed = polySlab(SQUARE, BED_H, MOSS, LIFT);
  if (s === 2) return [bed, ...flagstones(1)];
  return [
    bed,
    ...flagstones(3),
    ...edgingSetts(),
  ];
}

export const pavingV2 = defineModel({
  id: "paving-v2",
  tiles: { w: 1, h: 1 },
  batchId: "fable",
  name: "Paving (v2)",
  tags: ["building","paving","low-poly","age-of-mcp","fable"],
  summary: "Low-poly paving.",
  description: "A flat, walkable stone tile.",
  author: "age-of-mcp",
  footprint: 0.5,
  defaultVariant: "complete",
  variants: [
    { id: "stage-0", label: "Foundation", parts: () => stage(0) },
    { id: "stage-1", label: "Lower", parts: () => stage(1) },
    { id: "stage-2", label: "Upper", parts: () => stage(2) },
    { id: "complete", label: "Complete", parts: () => stage(3) },
  ],
  animations: [],
});
